<script setup>
  import Lucide from "@/base-components/Lucide";
  import { useFetch } from "@/composables/useFetch.js";
  import { onMounted, ref, watch } from "vue";
  import Vue3Datatable from "@bhplugin/vue3-datatable";
  import "@bhplugin/vue3-datatable/dist/style.css";
  import { useRoute, useRouter } from "vue-router";
  import { uri } from "@/constants/config.js";
  import UserDialog from "./UserDialog.vue";
  import "vue-awesome-paginate/dist/style.css";
  import BasePagination from "@/base-components/Table/BasePagination.vue";
  import { useI18n } from "vue-i18n";
  import moment from "moment-jalaali";
  import InputText from "@/base-components/Form/InputText.vue";
  import ConfirmationDialog from "@/base-components/Dialog/ConfirmationDialog.vue";
  import PasswordDialog from "@/pages/manager/users/list/PasswordDialog.vue";
  import ValidationDialog from "@/pages/manager/users/list/ValidationDialog.vue";
  import EditDialog from "@/pages/manager/users/list/EditDialog.vue";

  const { t: $t } = useI18n();
  const route = useRoute();
  const router = useRouter();
  const userList = ref([]);
  const isLoadingFlag = ref(true);
  const cols = [
    { field: "counter", title: "#" },
    { field: "id", title: $t("title.id") },
    { field: "firstName", title: $t("title.first-name") },
    { field: "lastName", title: $t("title.last-name") },
    { field: "email", title: $t("title.email") },
    { field: "mobileValidation", title: $t("title.mobile") },
    { field: "emailValidation", title: $t("title.email") },
    { field: "validation", title: $t("title.validation") },
    { field: "access", title: $t("title.access") },
    { field: "registerAt", title: $t("title.register-at") },
    { field: "setting", title: $t("title.setting") },
  ];
  const pageOptions = ref({
    count: 1,
    limit: 10,
    page: 1,
  });
  const queryKey = ref(null);
  const showToggleBlockUserFlag = ref(false);
  const showPasswordDialogFlag = ref(false);
  const showValidationDialogFlag = ref(false);
  const showEditDialogFlag = ref(false);
  const currentUser = ref({});

  function showToggleBlockUserDialog(user) {
    currentUser.value = user;
    showToggleBlockUserFlag.value = true;
  }

  function cancelToggleBlockUser() {
    showToggleBlockUserFlag.value = false;
  }

  async function confirmToggleBlockUser() {
    showToggleBlockUserFlag.value = false;
    isLoadingFlag.value = true;
    try {
      const { data, error } = await useFetch(
        `${uri.admin.user.block}/${currentUser.value.id}/toggle-block`,
        {
          method: "PATCH",
          body: { isBlocked: !currentUser.value.isBlocked },
        }
      );
    } catch (err) {
      console.log(err);
    }
    await fetchData();
  }

  function showPasswordDialog(user) {
    currentUser.value = user;
    showPasswordDialogFlag.value = true;
  }

  function showValidationDialog(user) {
    currentUser.value = user;
    showValidationDialogFlag.value = true;
  }

  function showEditDialog(user) {
    currentUser.value = user;
    showEditDialogFlag.value = true;
  }

  function onSuccessOpenUser() {
    setOpenUserFlag(false);
    fetchData();
  }

  const getUserList = async () => {
    isLoadingFlag.value = true;
    let response = null;
    try {
      const { data, error } = await useFetch(
        `${uri.admin.user.list}?page=${pageOptions.value.page}&q=${
          queryKey.value ?? ""
        }`,
        { method: "GET" }
      );
      if (data) {
        response = data;
        pageOptions.value.count = data.meta.total;
        pageOptions.value.limit = data.meta.perPage;
      }
    } catch (err) {
      console.log(err);
    }
    return response;
  };

  const fetchData = async () => {
    try {
      isLoadingFlag.value = true;
      const [user] = await Promise.all([getUserList()]);
      userList.value = user.data;
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      isLoadingFlag.value = false;
    }
  };

  const openUserFlag = ref(false);

  function setOpenUserFlag(value) {
    openUserFlag.value = value;
  }

  function openUserDialog() {
    openUserFlag.value = true;
  }

  function rowClick(user) {}

  // Fetch data when the component mounts
  onMounted(fetchData);
  watch(queryKey, (value, oldValue) => {
    if (value?.length > 3 || oldValue?.length > value?.length) {
      fetchData();
    }
  });
</script>
<template>
  <section>
    <UserDialog
      v-if="openUserFlag"
      @close="setOpenUserFlag(false)"
      @onSuccess="onSuccessOpenUser"
      :show="openUserFlag"
    />

    <div v-if="showPasswordDialogFlag">
      <PasswordDialog
        v-if="showPasswordDialogFlag"
        @close="showPasswordDialogFlag = false"
        @onSuccess="fetchData"
        :user="currentUser"
        :show="showPasswordDialogFlag"
        user=""
      />
    </div>

    <div v-if="showValidationDialogFlag">
      <ValidationDialog
        v-if="showValidationDialogFlag"
        @close="showValidationDialogFlag = false"
        @onSuccess="fetchData"
        :user="currentUser"
        :show="showValidationDialogFlag"
        user=""
      />
    </div>

    <div v-if="showEditDialogFlag">
      <EditDialog
        v-if="showEditDialogFlag"
        @close="showEditDialogFlag = false"
        @onSuccess="fetchData"
        :user="currentUser"
        :show="showEditDialogFlag"
        user=""
      />
    </div>

    <div v-if="showToggleBlockUserFlag">
      <ConfirmationDialog
        :close="cancelToggleBlockUser"
        :show="showToggleBlockUserFlag"
        :accept="confirmToggleBlockUser"
        :deny="cancelToggleBlockUser"
      >
        {{ $t("message.are-you-sure-change-status") }}
        {{ currentUser?.firstName ?? "" }}
      </ConfirmationDialog>
    </div>

    <div class="grid grid-cols-12 gap-6 mt-8">
      <div class="col-span-12 lg:col-span-12 2xl:col-span-12">
        <!-- BEGIN: Inbox Content -->
        <div class="mt-5 intro-y box">
          <div
            class="grid grid-cols-12 border-slate-200/60 border-b mt-2 gap-3 px-2"
          >
            <div
              class="col-span-12 lg:col-span-6 md:col-span-6 xl:col-span-6 2xl:col-span-6 pb-2 relative"
            >
              <Lucide icon="Search" class="w-6 h-6 absolute mt-2 mr-2" />
              <InputText
                :dir="'ltr'"
                name="query"
                :has-label="false"
                v-model="queryKey"
                id="input-wizard-3"
                type="text"
              />
            </div>
            <div
              class="col-span-12 lg:col-span-6 md:col-span-6 xl:col-span-6 2xl:col-span-6"
            >
              <div
                class="flex px-2 pt-2.5 sm:flex-row text-slate-500 text-end w-full justify-end items-end"
              >
                <div class="w-5 h-5 ms-5 cursor-pointer" @click="fetchData">
                  <Lucide icon="RefreshCw" class="w-6 h-6" />
                </div>
                <div
                  class="w-5 h-5 ms-5 cursor-pointer"
                  @click="openUserDialog"
                >
                  <Lucide icon="PlusCircle" class="w-6 h-6" />
                </div>
              </div>
            </div>
          </div>
          <div class="mb-5 relative"></div>
          <vue3-datatable
            :columns="cols"
            :rows="userList"
            :loading="isLoadingFlag"
            :totalRows="pageOptions.count"
            :isServerMode="true"
            :pageSize="pageOptions.limit"
            :pagination="false"
            :sortable="false"
            class="advanced-table px-4 text-start items-start justify-start"
            rowClass="cursor-pointer"
            @rowClick="rowClick"
          >
            <!-- Empty header slot to remove the header -->
            <template #header>
              <!-- No content here will hide the header -->
            </template>
            <!-- Empty footer slot to remove the footer -->
            <template #footer>
              <!-- No content here will hide the footer -->
            </template>
            <template #counter="data, key">
              <div class="text-start">
                {{
                  userList.indexOf(data.value) +
                  1 +
                  (pageOptions.page - 1) * pageOptions.limit
                }}
              </div>
            </template>
            <template #id="data, key">
              <div class="text-start">
                {{ data.value.userId }}
              </div>
            </template>
            <template #firstName="data, key">
              <div class="text-start">
                {{ data.value.Profile.firstName }}
              </div>
            </template>
            <template #lastName="data, key">
              <div class="text-start">
                {{ data.value.Profile.lastName }}
              </div>
            </template>
            <template #email="data, key">
              <div dir="ltr">
                {{ data.value.email }}
              </div>
            </template>
            <template #validation="data, key">
              <div class="text-start">
                <Lucide
                  icon="Verified"
                  class="w-6 h-6 text-gray-700"
                  v-if="data.value.Profile.isNationalCodeValidated"
                />
                <Lucide icon="Loader2" class="w-6 h-6 text-gray-700" v-else />
              </div>
            </template>
            <template #access="data, key">
              <div
                class="text-start"
                @click="showToggleBlockUserDialog(data.value)"
              >
                <Lucide
                  icon="MinusCircle"
                  class="w-6 h-6 text-gray-700"
                  v-if="data.value.isBlocked"
                />
                <Lucide icon="Unlock" class="w-6 h-6 text-gray-700" v-else />
              </div>
            </template>
            <template #mobileValidation="data, key">
              <div class="text-start">
                <Lucide
                  icon="Verified"
                  class="w-6 h-6 text-gray-700"
                  v-if="data.value.isMobileValidated"
                />
                <Lucide icon="Loader2" class="w-6 h-6 text-gray-700" v-else />
              </div>
            </template>
            <template #emailValidation="data, key">
              <div class="text-start">
                <Lucide
                  icon="Verified"
                  class="w-6 h-6 text-gray-700"
                  v-if="data.value.isEmailValidated"
                />
                <Lucide icon="Loader2" class="w-6 h-6 text-gray-700" v-else />
              </div>
            </template>
            <template #registerAt="data, key">
              <div class="text-end" dir="ltr">
                {{
                  data.value.Profile.createdAt
                    ? moment(data.value.Profile.createdAt)
                        .locale("fa")
                        .format("jYYYY/jMM/jDD HH:mm")
                    : "-"
                }}
              </div>
            </template>
            <template #setting="data, key">
              <div class="text-start flex gap-3">
                <div @click="showPasswordDialog(data.value)">
                  <Lucide icon="Fingerprint" class="w-6 h-6 text-gray-700" />
                </div>
                <div @click="showValidationDialog(data.value)">
                  <Lucide icon="ShieldAlert" class="w-6 h-6 text-gray-700" />
                </div>
                <div @click="showEditDialog(data.value)">
                  <Lucide icon="Edit" class="w-6 h-6 text-gray-700" />
                </div>
              </div>
            </template>
          </vue3-datatable>
          <div
            class="flex flex-col items-center p-5 text-center sm:flex-row sm:text-left text-slate-500"
          >
            <div>
              <BasePagination
                v-if="pageOptions.count > pageOptions.limit"
                :page-options="pageOptions"
                @page-change="fetchData"
              />
            </div>
            <div class="mt-2 sm:ml-auto sm:mt-0"></div>
          </div>
        </div>
        <!-- END: Inbox Content -->
      </div>
    </div>
  </section>
</template>
